<template>
  <ul
    class="option_tray"
    :class="{
      'option_tray--top': position === 'top',
    }"
    ref="tray"
  >
    <svg
      class="option_tray__chip_icon"
      width="14px"
      height="6px"
      viewBox="0 0 14 6"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
    >
      <g
        id="Symbols"
        stroke="none"
        stroke-width="1"
        fill="none"
        fill-rule="evenodd"
      >
        <g
          id="Atom/Inputs/Dropdown/Dropdown-Tray"
          transform="translate(-81.000000, 0.000000)"
          fill="#FFFFFF"
        >
          <g id="Indicator-Arrow" transform="translate(81, 0.000000)">
            <path
              d="M1.38177509,5.07
                            L11.6160548,5.07
                            L6.67001757,0.67
                            L1.38177509,5.07 Z"
              id="Path"
              stroke="#C7C7CC"
            ></path>
            <polygon id="Path-2" points="1 6 6.66245569 1.3 11.8 6"></polygon>
          </g>
        </g>
      </g>
    </svg>
    <li v-if="searchAble" class="option_tray__searchbar">
      <SearchBar
        ref="searchBar"
        :placeholder="searchbarPlaceholder"
        class="option_tray__searchbar_input"
        v-on:input="searchValue = $event"
        v-on:blur="onSearchBarBlur"
      />
    </li>
    <div :class="{ option_tray_items: searchAble }">
      <li
        v-for="(option, index) in processedOptions"
        :key="index"
        class="option_tray__option"
        :class="{
          'option_tray__option--focussed': focussedOptionIndex === index,
          'option_tray__option--placeholder': option.placeholder,
        }"
        @keydown.arrow-down.prevent="focusNext"
        @keydown.arrow-up.prevent="focusPrevious"
        @mouseover="setFocussed(index, false)"
        @click="
          emitOption(index);
          requestClose();
        "
      >
        {{ option.displayValue || option.value }}
      </li>
    </div>
    <li v-if="processedOptions.length === 0" class="option_tray__option">
      {{ labelNoResults }}
    </li>
  </ul>
</template>

<script>
import Fuse from "fuse.js";
import BaseComponent from "../BaseComponent";
import SearchBar from "./Inputs/SearchBar.vue";

/**
 * @group Inputs - parts
 * This is a tray in which options are presented. It has full and proper keyboard control.
 * This is almost always used as part of another component, for example: the TextInput with autocomplete.
 */
export default {
  name: "OptionTray",
  mixins: [BaseComponent],
  components: {
    SearchBar,
  },
  props: {
    searchAble: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      required: true,
    },
    parentValue: {
      required: false,
    },
    // If you pass the placeholder to this component, it'll add a grey placeholder option in the tray
    // this means the user can select this placeholder and unselect an option.
    placeholder: {
      type: String,
      required: false,
    },
    visibleItems: {
      type: Number,
      required: false,
      default: 3,
    },
    /**
     * Placeholder text to be displayed in the searchbar if present
     */
    searchbarPlaceholder: {
      type: String,
      default: "Search",
    },
    labelNoResults: {
      type: String,
      default: "No result",
    },
  },
  data() {
    return {
      focussedOptionIndex: 0,
      position: "bottom",
      searchValue: "",
      numberOfVisibleItems: null,
      fuzzysearchOptions: {
        shouldSort: true,
        threshold: 0.4,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: ["displayValue"],
      },
    };
  },
  watch: {
    options() {
      this.focussedOptionIndex = 0;
    },
  },
  computed: {
    processedOptions() {
      let processedOptions = [...this.options];
      if (this.searchAble && this.searchValue) {
        processedOptions = this.fuseSearch
          .search(this.searchValue)
          .map((result) => ({
            displayValue: result.item.displayValue,
            value: result.item.value,
          }));
      }
      if (this.placeholder) {
        processedOptions.unshift({
          displayValue: this.placeholder,
          value: null,
          placeholder: true,
        });
      }
      return processedOptions;
    },
    fuseSearch() {
      if (!this.searchAble) return;
      // eslint-disable-next-line consistent-return
      return new Fuse(this.options, this.fuzzysearchOptions);
    },
  },
  methods: {
    // @vuese
    // When an option is selected, the component emits that option.
    emitOption(index) {
      this.$emit("setOption", this.processedOptions[index]);
    },
    setFocussed(index, emit) {
      let optionIndex = index;
      if (index < 0) optionIndex = this.processedOptions.length - 1;
      if (index > this.processedOptions.length - 1) optionIndex = 0;
      this.focussedOptionIndex = optionIndex;
      if (emit !== false) this.emitOption(optionIndex);
    },
    focusNext() {
      this.setFocussed(this.focussedOptionIndex + 1);
    },
    focusPrevious() {
      this.setFocussed(this.focussedOptionIndex - 1);
    },
    requestClose() {
      this.$emit("requestClose");
    },
    // Needs to be called from parent element using vue $refs,
    // since we want the user to be able to type in a textbox
    // whilst also navigating options.
    keydownHandler(key) {
      switch (key) {
        case "ArrowDown":
          this.focusNext();
          break;
        case "ArrowUp":
          this.focusPrevious();
          break;
        case "Enter":
          this.emitOption(this.focussedOptionIndex);
          this.requestClose();
          break;
        default:
          break;
      }
    },
    setParentValue() {
      if (!this.parentValue) return;
      this.focussedOptionIndex = this.processedOptions
        .map((e) => e.value)
        .indexOf(this.parentValue);
      this.setFocussed(this.focussedOptionIndex);
    },
    // @vuese
    // Check if the tray should open to the bottom or to the top of it's parent element.
    // If there is space on the top and it's falling of the bottom, open it to the top.
    // Else open it to the bottom.
    checkBoundaries() {
      if (typeof window === "undefined") return;
      const bottomOverflow =
        window.innerHeight -
        (this.$el.getBoundingClientRect().top +
          this.$el.getBoundingClientRect().height);
      const parent = this.$parent.$el;
      const topOverflow =
        parent.getBoundingClientRect().top -
        this.$el.getBoundingClientRect().height;
      if (bottomOverflow < 0 && topOverflow > 0) {
        this.position = "top";
      } else {
        this.position = "bottom";
      }
    },
    onSearchBarBlur(event) {
      if (this.$refs.tray.parentElement !== event.relatedTarget) {
        this.requestClose();
      }
    },
  },
  mounted() {
    this.setParentValue();
    this.checkBoundaries();
    if (this.searchAble) {
      this.$refs.searchBar.$el.focus();
    }
    if (this.searchAble) {
      this.numberOfVisibleItems = this.visibleItems;
      if (this.placeholder) {
        this.numberOfVisibleItems += 1;
      }
      document.documentElement.style.setProperty(
        "--options-height",
        this.numberOfVisibleItems
          ? `${this.numberOfVisibleItems * 44}px`
          : "100%"
      );
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../style_variables/style_variables.scss";
$dropdown_padding: 14px;

.option_tray {
  @include elevationLevel(2);
  z-index: 33;
  position: absolute;
  width: 100%;
  top: 100%;
  left: 0px;
  display: inline-flex;
  flex-direction: column;
  margin: 12px 0px 0px;
  padding: 0px;
  background: $white;
  border: 1px solid $grey_french;
  border-radius: 4px;
  list-style: none;

  @media (max-width: 769px) {
    top: 0%;
  }
}
.option_tray--top {
  top: auto;
  bottom: 100%;
  margin: 0px 0px 12px;
  .option_tray__chip_icon {
    top: auto;
    bottom: -6px;
    transform: rotate(180deg);
  }
}
.option_tray__option {
  @extend %body2_style;
  padding: 12px 25px;
  color: $navy;
  outline: 0;
  &:hover {
    cursor: pointer;
  }
  &.option_tray__option--focussed {
    @extend %button_typography_style;
    background-color: rgba(67, 139, 248, 0.1);
  }
  &.option_tray__option--placeholder {
    color: $grey_french;
    &:hover {
      @extend %button_typography_style;
      background-color: rgba(67, 139, 248, 0.1);
    }
  }
}
.option_tray__chip_icon {
  position: absolute;
  left: 50%;
  top: -6px;
}

.option_tray__searchbar {
  display: flex;
  margin: 18px 25px;
  background-color: $white;
  border-radius: 6px;
}
.option_tray__searchbar_icon {
  @extend %body2_style;
  width: 24px;
  height: 48px;
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
.option_tray__searchbar_input {
  @extend %body2_style;
  width: 100%;
  height: 48px;
  padding-left: 8px;
  border-radius: 4px;
  border: 0px;
  outline: 0;
}
.option_tray_items {
  max-height: var(--options-height);
  overflow-y: auto;
}
</style>
